import { ComponentFactoryResolver, Injectable, Injector } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import {
    CustomerBillingInvoiceModel,
    ICustomerBillingInvoiceDate,
    ICustomerBillingInvoiceStatusData,
} from './customer-billing-invoice.model';
import { CustomerBillingInvoiceDetailModel } from './customer-billing-invoice-detail.model';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { fiInvoiceMethodType } from './invoice-method-type';
import { AppTextService } from '@fi-shared/app-text';
import { HtmlPreviewService } from '@fi-shared/fi-print-download/html-preview.service';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { CustomerBillingInvoiceViewInvoiceComponent } from './customer-billing-invoice-view-invoice/customer-billing-invoice-view-invoice.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { isFunction } from '@fi-shared/utils/is-function';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Subject } from 'rxjs';

@Injectable()
export class InvoiceService {
    constructor(
        private fiBackend: FiBackendService,
        private fiLoadingService: LoadingService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private htmlPreviewService: HtmlPreviewService,
    ) {}

    CreateInvoice() {
        return new CustomerBillingInvoiceModel(undefined);
    }

    GetInvoicesCount(parameterApiModel) {
        return this.fiBackend.fetchDataPost<number>(
            'CustomerBilling/CustomerBilling/GetInvoicesCount',
            parameterApiModel,
        );
    }

    GetInvoices(parameterApiModel) {
        return this.fiBackend.fetchObjectsPost(
            'CustomerBilling/CustomerBilling/GetInvoices',
            CustomerBillingInvoiceModel,
            parameterApiModel,
        );
    }

    GetLightWeightObjectsForLocalSearch() {
        return this.fiBackend.fetchData<ILightWeightObjectCollection>(
            'CustomerBilling/CustomerBilling/GetLightWeightObjectsForInvoicesLocalSearch',
        );
    }

    SaveInvoice(invoice: CustomerBillingInvoiceModel) {
        return this.fiBackend.fetchDataPost('CustomerBilling/CustomerBilling/SaveInvoice', invoice);
    }

    GetInvoice(id) {
        return this.fiBackend.fetchObject(
            'CustomerBilling/CustomerBilling/GetInvoice/' + id,
            CustomerBillingInvoiceModel,
        );
    }

    GetMonthlyInvoicePredefinedArticles(customerId) {
        return this.fiBackend.fetchObjects(
            'CustomerBilling/CustomerBilling/GetMonthlyInvoicePredefinedArticles/' + customerId,
            CustomerBillingInvoiceDetailModel,
        );
    }

    GetNonModulePriceArticles(customerId) {
        return this.fiBackend.fetchObjects(
            'CustomerBilling/CustomerBilling/GetNonModulePriceArticles/' + customerId,
            CustomerBillingInvoiceDetailModel,
        );
    }

    private printN = 0;

    printInvoice(invoice: CustomerBillingInvoiceModel, injector: Injector) {
        const promise = new Promise<ICustomerBillingInvoiceDate>((resolve, reject) => {
            this.renderInvoice(invoice, injector).then((html) => {
                this.openPrintDialog(invoice.Id, html).then(() => {
                    setTimeout(() => {
                        this.fiBackend
                            .fetchDataPost<ICustomerBillingInvoiceDate>(
                                'CustomerBilling/CustomerBilling/SetPrintedStateToInvoice/' + invoice.Id,
                            )
                            .then(resolve, reject);
                    }, 500);
                }, reject);
            }, reject);
        });

        return this.fiLoadingService.trackPromise(promise);
    }
    /*cancelInvoicePrintedState(invoiceId: number) {
        return this.fiBackend.fetchDataPost<ICustomerBillingInvoiceStatusData>('CustomerBilling/CustomerBilling/CancelInvoicePrintedState/' + invoiceId);
    }*/

    sendInvoice(invoice: CustomerBillingInvoiceModel, injector: Injector) {
        const promise = new Promise<ICustomerBillingInvoiceDate>((resolve, reject) => {
            this.renderInvoice(invoice, injector).then((html) => {
                this.fiBackend
                    .fetchDataPost<ICustomerBillingInvoiceDate>('CustomerBilling/CustomerBilling/SendInvoice', {
                        InvoiceHtml: html,
                        Id: invoice.Id,
                    })
                    .then(resolve, reject);
            });
        });
        promise.then(() => {
            this.fiDialogsService.notify({
                messageText: this.appTextService.get('customer_billing.invoice_sent'),
                headerText: this.appTextService.get('common.information'),
            });
        });

        return this.fiLoadingService.trackPromise(promise);
    }
    /*cancelInvoiceSentState(invoiceId: number) {
        return this.fiBackend.fetchDataPost<ICustomerBillingInvoiceStatusData>('CustomerBilling/CustomerBilling/CancelInvoiceSentState/' + invoiceId);
    }*/

    payInvoice(invoice: CustomerBillingInvoiceModel) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<ICustomerBillingInvoiceStatusData>(
                'CustomerBilling/CustomerBilling/PayInvoice/' + invoice.Id,
            ),
        );
    }

    cancelInvoicePaidState(invoiceId: number) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<ICustomerBillingInvoiceStatusData>(
                'CustomerBilling/CustomerBilling/CancelInvoicePaidState/' + invoiceId,
            ),
        );
    }

    private renderInvoice(invoice: CustomerBillingInvoiceModel, injector: Injector) {
        const promise = new Promise<string>((resolve, reject) => {
            let resolver = injector.get(ComponentFactoryResolver);

            const compFactory = resolver.resolveComponentFactory(CustomerBillingInvoiceViewInvoiceComponent);
            const compRef = compFactory.create(injector, null);
            compRef.instance.invoice = invoice;
            compRef.changeDetectorRef.detectChanges();

            const el = document.createElement('div');
            el.classList.add('print-hide');
            el.innerHTML = compRef.location.nativeElement.innerHTML;
            document.body.appendChild(el);
            resolve(this.htmlPreviewService.getPreviewHtml(el.firstChild as HTMLElement));
            el.remove();
            compRef.destroy();
        });
        return promise;
    }

    private getFormPrintElement(requestUrl: string): HTMLFormElement {
        const form: HTMLFormElement = document.createElement('form');
        form.setAttribute('action', requestUrl);
        form.setAttribute('target', 'printDialog');
        form.setAttribute('enctype', 'application/json');
        form.setAttribute('method', 'post');
        return form;
    }

    private getInputElement(inputValue: string, nameAttrValue: string): HTMLInputElement {
        const inputElement: HTMLInputElement = document.createElement('input');
        inputElement.setAttribute('name', nameAttrValue);
        inputElement.setAttribute('type', 'hidden');
        inputElement.value = inputValue;
        return inputElement;
    }

    private openPrintDialog(invoiceId: number, html: string) {
        const promise = new Promise<void>((resolve, reject) => {
            const formElement: HTMLFormElement = this.getFormPrintElement(
                this.fiBackend.getRequestUrl('CustomerBilling/CustomerBilling/GetInvoicePdf'),
            );
            formElement.append(this.getInputElement(html, 'InvoiceHtml'));
            formElement.append(this.getInputElement(invoiceId.toString(), 'Id'));
            formElement.append(this.getInputElement(`invoice.pdf`, 'Name'));
            document.body.appendChild(formElement);
            formElement.submit();
            formElement.remove();

            resolve();
        });

        return promise;
    }

    private SendInvoice(invoice: CustomerBillingInvoiceModel, injector: Injector) {
        const promise = new Promise<{ DeliveryType: number; Html: string }>((resolve, reject) => {
            this.renderInvoice(invoice, injector).then((html) => {
                this.fiBackend
                    .fetchDataPost<number>('CustomerBilling/CustomerBilling/SendInvoice', {
                        InvoiceHtml: html,
                        Id: invoice.Id,
                    })
                    .then((deliveryType) =>
                        resolve({
                            DeliveryType: deliveryType,
                            Html: html,
                        }),
                    )
                    .catch((err) => reject(err));
            });
        });
        return promise;
    }

    SendAndPrintInvoice(invoice: CustomerBillingInvoiceModel, injector: Injector) {
        const promise = new Promise<{ DeliveryType: number; Html: string }>((resolve, reject) => {
            this.SendInvoice(invoice, injector).then((result) => {
                resolve(result);

                this.fiDialogsService.notify({
                    messageText: this.appTextService.get('customer_billing.invoice_sent'),
                    headerText: this.appTextService.get('common.information'),
                });
            }, reject);
        });

        return this.fiLoadingService.trackPromise(promise);
    }

    private _invoiceStatusChanged$ = new Subject<number>();
    get invoiceStatusChanged$() {
        return this._invoiceStatusChanged$.asObservable();
    }
    triggerInvoiceStatusChanged(invoiceId: number) {
        this._invoiceStatusChanged$.next(invoiceId);
    }
}
